/** CodeMirror 6 editor configured for LaTeX. */
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { StreamLanguage, bracketMatching, indentUnit } from '@codemirror/language';
import { stex } from '@codemirror/legacy-modes/mode/stex';
import { highlightSelectionMatches, searchKeymap } from '@codemirror/search';
import { EditorState } from '@codemirror/state';
import {
  EditorView,
  drawSelection,
  highlightActiveLine,
  highlightActiveLineGutter,
  keymap,
  lineNumbers,
} from '@codemirror/view';

export interface Editor {
  getText(): string;
  setText(text: string): void;
  gotoLine(line: number): void;
  focus(): void;
}

export function createEditor(
  parent: HTMLElement,
  initial: string,
  hooks: { onChange: (text: string) => void; onCompileRequest: () => void },
): Editor {
  const view = new EditorView({
    parent,
    state: EditorState.create({
      doc: initial,
      extensions: [
        lineNumbers(),
        highlightActiveLine(),
        highlightActiveLineGutter(),
        history(),
        drawSelection(),
        bracketMatching(),
        highlightSelectionMatches(),
        indentUnit.of('  '),
        EditorView.lineWrapping,
        StreamLanguage.define(stex),
        keymap.of([
          {
            key: 'Mod-Enter',
            run: () => {
              hooks.onCompileRequest();
              return true;
            },
          },
          ...defaultKeymap,
          ...historyKeymap,
          ...searchKeymap,
          indentWithTab,
        ]),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) hooks.onChange(update.state.doc.toString());
        }),
      ],
    }),
  });

  return {
    getText: () => view.state.doc.toString(),
    setText: (text) => {
      view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: text } });
    },
    gotoLine: (line) => {
      const clamped = Math.max(1, Math.min(line, view.state.doc.lines));
      const pos = view.state.doc.line(clamped).from;
      view.dispatch({ selection: { anchor: pos }, scrollIntoView: true });
      view.focus();
    },
    focus: () => {
      view.focus();
    },
  };
}
